import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chip } from "@legal-500/legal500-design-system";
import { packages, type PackageWithFeatureCodes } from "../../api/packages.service";
import styles from "./PackageVersionHistory.module.css";

export type PackageVersionHistoryProps = {
  productCode: string;
  currentVersionId: string;
};

export function PackageVersionHistory({
  productCode,
  currentVersionId,
}: PackageVersionHistoryProps) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<PackageWithFeatureCodes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [productCode]);

  async function loadVersions() {
    try {
      setLoading(true);
      setError(null);
      const versionsList = await packages.getVersionsByProductCode(productCode);
      setVersions(versionsList);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load version history";
      setError(`Failed to load versions for ${productCode}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }

  function handleVersionClick(version: PackageWithFeatureCodes) {
    navigate(`/fulfilment/packages/${version.id}`);
  }

  function formatDate(dateString: string | Date): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  if (loading) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Version History</h2>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Loading versions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Version History</h2>
        <div className={styles.errorContainer}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.errorIcon}
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Version History</h2>
        <p className={styles.emptyText}>No versions found</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Version History ({versions.length})</h2>
      <div className={styles.timeline}>
        {versions.map(version => {
          const isCurrent = version.id === currentVersionId;
          const featureCount = version.featureCodes?.length || 0;

          return (
            <div
              key={version.id}
              className={`${styles.timelineItem} ${isCurrent ? styles.currentVersion : ""}`}
              onClick={() => handleVersionClick(version)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleVersionClick(version);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`View version ${version.version}`}
            >
              <div className={styles.timelineMarker}>
                <div className={styles.markerDot} />
                {isCurrent && <div className={styles.markerPulse} />}
              </div>

              <div className={styles.versionCard}>
                <div className={styles.versionHeader}>
                  <div className={styles.versionInfo}>
                    <span className={styles.versionNumber}>v{version.version}</span>
                  </div>
                  <div className={styles.badges}>
                    <Chip
                      size="small"
                      variant="outlined"
                      className={version.isActive ? styles.statusActive : styles.statusInactive}
                    >
                      {version.isActive ? "Active" : "Inactive"}
                    </Chip>
                  </div>
                </div>

                <div className={styles.versionDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created</span>
                    <span className={styles.detailValue}>{formatDate(version.createdAt)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Features</span>
                    <span className={styles.detailValue}>{featureCount}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
